import { useCallback, useEffect, useState } from 'react';
import {
  inventoryintelApi,
  type DemandForecast,
  type InventoryAlert,
  type InventoryDashboard,
  type InventoryHealthRecord,
  type InventoryRecommendation,
  type ReplenishmentPlan,
  type RiskSummary,
  type ScenarioRecord,
  type SupplierRecord,
} from '../../api/inventoryintel';

type Tab = 'overview' | 'forecasts' | 'replenishment' | 'suppliers' | 'recommendations' | 'scenarios' | 'risk' | 'alerts';

function riskClass(r: string) {
  if (r === 'critical') return 'text-red-700 bg-red-50';
  if (r === 'high') return 'text-orange-700 bg-orange-50';
  if (r === 'medium') return 'text-amber-700 bg-amber-50';
  return 'text-emerald-700 bg-emerald-50';
}

function severityClass(s: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50 border-red-200';
  if (s === 'high') return 'text-orange-700 bg-orange-50 border-orange-200';
  if (s === 'medium') return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-slate-600 bg-slate-50 border-slate-200';
}

export function InventoryIntelPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<InventoryDashboard | null>(null);
  const [health, setHealth] = useState<InventoryHealthRecord[]>([]);
  const [forecasts, setForecasts] = useState<DemandForecast[]>([]);
  const [replenishment, setReplenishment] = useState<ReplenishmentPlan[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierRecord[]>([]);
  const [recommendations, setRecommendations] = useState<InventoryRecommendation[]>([]);
  const [scenarios, setScenarios] = useState<ScenarioRecord[]>([]);
  const [alerts, setAlerts] = useState<InventoryAlert[]>([]);
  const [risk, setRisk] = useState<RiskSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, h, fc, rp, sup, recs, sc, al, rk] = await Promise.all([
        inventoryintelApi.dashboard(),
        inventoryintelApi.listHealth(),
        inventoryintelApi.listForecasts(),
        inventoryintelApi.listReplenishment(),
        inventoryintelApi.listSuppliers(),
        inventoryintelApi.listRecommendations(),
        inventoryintelApi.listScenarios(),
        inventoryintelApi.listAlerts(),
        inventoryintelApi.riskSummary(),
      ]);
      setDashboard(dash);
      setHealth(h);
      setForecasts(fc);
      setReplenishment(rp);
      setSuppliers(sup);
      setRecommendations(recs);
      setScenarios(sc);
      setAlerts(al);
      setRisk(rk);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load inventory intelligence');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try { await inventoryintelApi.generateRecommendations(); await refresh(); }
    finally { setBusy(false); }
  };

  const runScenario = async (type: string, name: string) => {
    setBusy(true);
    try { await inventoryintelApi.runScenario({ name, type }); await refresh(); }
    finally { setBusy(false); }
  };

  const ackAlert = async (id: string) => {
    setBusy(true);
    try { await inventoryintelApi.acknowledgeAlert(id); await refresh(); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Inventory Dashboard' },
    { id: 'forecasts', label: 'Forecast Center' },
    { id: 'replenishment', label: 'Replenishment Planner' },
    { id: 'suppliers', label: 'Supplier Console' },
    { id: 'recommendations', label: 'Recommendation Center' },
    { id: 'scenarios', label: 'Scenario Simulator' },
    { id: 'risk', label: 'Risk Dashboard' },
    { id: 'alerts', label: 'Alerts' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Enterprise Inventory Intelligence</h1>
          <p className="text-sm text-slate-500">Demand forecasting, replenishment planning &amp; AI copilot — BCP-00003</p>
        </div>
        <button type="button" onClick={refresh} disabled={busy}
          className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">
          Refresh
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-teal-100 text-teal-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Total SKUs', value: dashboard.total_skus.toString() },
              { label: 'Total Units', value: dashboard.total_units.toLocaleString() },
              { label: 'Avg Days of Cover', value: dashboard.avg_days_of_cover.toFixed(1) },
              { label: 'Health Score', value: `${dashboard.avg_health_score.toFixed(0)}%` },
              { label: 'Stockout Risk SKUs', value: dashboard.stockout_risk_count.toString() },
              { label: 'Overstock Risk SKUs', value: dashboard.overstock_risk_count.toString() },
              { label: 'Active Alerts', value: dashboard.active_alerts.toString() },
              { label: 'Forecast Accuracy', value: `${dashboard.forecast_accuracy_pct}%` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 className="font-medium text-slate-900">Inventory Health</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2">SKU</th>
                    <th className="px-4 py-2">Domain</th>
                    <th className="px-4 py-2">On Hand</th>
                    <th className="px-4 py-2">Days Cover</th>
                    <th className="px-4 py-2">Stockout</th>
                    <th className="px-4 py-2">Health</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {health.map((h) => (
                    <tr key={h.id}>
                      <td className="px-4 py-2 font-medium">{h.sku}</td>
                      <td className="px-4 py-2 capitalize">{h.domain.replace('_', ' ')}</td>
                      <td className="px-4 py-2">{h.on_hand.toLocaleString()}</td>
                      <td className="px-4 py-2">{h.days_of_cover.toFixed(0)}d</td>
                      <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs font-medium ${riskClass(h.stockout_risk)}`}>{h.stockout_risk}</span></td>
                      <td className="px-4 py-2">{h.health_score.toFixed(0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === 'forecasts' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Horizon</th>
                <th className="px-4 py-2">Units</th>
                <th className="px-4 py-2">Growth</th>
                <th className="px-4 py-2">Confidence</th>
                <th className="px-4 py-2">Factors</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {forecasts.map((f) => (
                <tr key={f.id}>
                  <td className="px-4 py-2 font-medium">{f.sku}</td>
                  <td className="px-4 py-2">{f.horizon}</td>
                  <td className="px-4 py-2">{f.units.toLocaleString()}</td>
                  <td className="px-4 py-2">{f.growth_pct > 0 ? '+' : ''}{f.growth_pct}%</td>
                  <td className="px-4 py-2">{(f.confidence * 100).toFixed(0)}%</td>
                  <td className="px-4 py-2 text-slate-500">{f.factors?.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'replenishment' && (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Purchase Qty</th>
                <th className="px-4 py-2">Safety Stock</th>
                <th className="px-4 py-2">Reorder Date</th>
                <th className="px-4 py-2">Urgency</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {replenishment.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2 font-medium">{r.sku}</td>
                  <td className="px-4 py-2">{r.purchase_qty.toLocaleString()}</td>
                  <td className="px-4 py-2">{r.safety_stock.toLocaleString()}</td>
                  <td className="px-4 py-2">{new Date(r.reorder_date).toLocaleDateString()}</td>
                  <td className="px-4 py-2"><span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${riskClass(r.urgency === 'critical' ? 'critical' : r.urgency === 'high' ? 'high' : 'low')}`}>{r.urgency ?? 'normal'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'suppliers' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {suppliers.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{s.name}</h3>
              <dl className="mt-3 space-y-1 text-sm text-slate-600">
                <div className="flex justify-between"><dt>Lead Time</dt><dd>{s.lead_time_days} days</dd></div>
                <div className="flex justify-between"><dt>MOQ</dt><dd>{s.moq.toLocaleString()}</dd></div>
                <div className="flex justify-between"><dt>Reliability</dt><dd>{(s.reliability_score * 100).toFixed(0)}%</dd></div>
                <div className="flex justify-between"><dt>Capacity</dt><dd>{s.capacity_units?.toLocaleString() ?? '—'}</dd></div>
              </dl>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <button type="button" onClick={generateRecs} disabled={busy}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">
            Generate AI Recommendations
          </button>
          {recommendations.map((rec) => (
            <div key={rec.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className="text-xs font-medium text-teal-600">#{rec.priority_rank} · {rec.type.replace(/_/g, ' ')}</span>
                  <h3 className="font-medium text-slate-900">{rec.title}</h3>
                  {rec.description && <p className="mt-1 text-sm text-slate-600">{rec.description}</p>}
                  {rec.evidence && <p className="mt-1 text-xs text-slate-500">{rec.evidence}</p>}
                </div>
                <div className="text-right text-sm">
                  <p className="font-medium">{(rec.confidence_score * 100).toFixed(0)}% confidence</p>
                  {rec.requires_approval && <p className="text-amber-600 text-xs">Requires approval</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'scenarios' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { type: 'prime_day', label: 'Prime Day' },
              { type: 'increase_advertising', label: 'Increase Advertising' },
              { type: 'supplier_delay', label: 'Supplier Delay' },
              { type: 'demand_spike', label: 'Demand Spike' },
            ].map((s) => (
              <button key={s.type} type="button" disabled={busy} onClick={() => runScenario(s.type, s.label)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50">
                Simulate: {s.label}
              </button>
            ))}
          </div>
          {scenarios.map((sc) => (
            <div key={sc.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{sc.name}</h3>
              <p className="mt-1 text-sm text-slate-600">{sc.outcome}</p>
              <div className="mt-2 flex gap-4 text-xs text-slate-500">
                <span>Stockout risk: {((sc.stockout_risk ?? 0) * 100).toFixed(0)}%</span>
                <span>Days of cover: {sc.days_of_cover?.toFixed(0)}d</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'risk' && risk && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-medium text-slate-900">Stockout Risk Distribution</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {Object.entries(risk.stockout_risk).map(([level, count]) => (
                <li key={level} className="flex justify-between">
                  <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${riskClass(level === 'none' ? 'low' : level)}`}>{level}</span>
                  <span>{count} SKUs</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="font-medium text-slate-900">Overstock Risk Distribution</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {Object.entries(risk.overstock_risk).map(([level, count]) => (
                <li key={level} className="flex justify-between">
                  <span className="capitalize">{level}</span>
                  <span>{count} SKUs</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'alerts' && (
        <div className="space-y-3">
          {alerts.map((a) => (
            <div key={a.id} className={`rounded-xl border p-4 ${severityClass(a.severity)} ${a.acknowledged ? 'opacity-60' : ''}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className="text-xs font-medium uppercase">{a.severity}</span>
                  <h3 className="font-medium">{a.title}</h3>
                  {a.description && <p className="mt-1 text-sm">{a.description}</p>}
                </div>
                {!a.acknowledged && (
                  <button type="button" onClick={() => ackAlert(a.id)} disabled={busy}
                    className="rounded-lg bg-white px-3 py-1 text-xs font-medium border hover:bg-slate-50">
                    Acknowledge
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
